import {
  Button,
  Card,
  Form,
  InputNumber,
  message,
  Select,
  Space,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ContentSkeleton from "../../../components/common/ContentSkeleton";
import { PERMISSIONS } from "../../../constants/permissions";
import { salesApi, sellerApi } from "../../../services/api";
import { AppDispatch, RootState } from "../../../store";
import { updateTokenBalance } from "../../../store/slices/authSlice";
import { SellerBasicInfo } from "../../../types/seller.types";
const { Option } = Select;
const { Text } = Typography;

interface ShareTokenForm {
  receiverId: string;
  tokenAmount: number;
}

const TokenDistributionPage: React.FC = () => {
  const [form] = Form.useForm<ShareTokenForm>();
  const [loading, setLoading] = useState(false);
  const [loadingReceivers, setLoadingReceivers] = useState(false);
  const [receivers, setReceivers] = useState<SellerBasicInfo[]>([]);
  const { user, permissions } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const canShareToken = permissions.some(
    (permission: any) => permission.name === PERMISSIONS.DISTRIBUTE_TOKEN.name
  );

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    await fetchSellers();
  };

  const fetchSellers = async () => {
    try {
      setLoadingReceivers(true);
      const response = await sellerApi.getSellers();
      if (response.status === "SUCCESS") {
        setReceivers(response.data);
      } else {
        message.error(response.message || "Failed to fetch receivers");
      }
    } catch (error) {
      message.error("Failed to fetch receivers");
    } finally {
      setLoadingReceivers(false);
    }
  };

  const handleReceiverChange = (value: string) => {
    console.log(`selected receiver ${value}`);
  };

  const onFinish = async (values: ShareTokenForm) => {
    try {
      setLoading(true);
      const response = await salesApi.distributeToken({
        receiverId: parseInt(values.receiverId),
        tokenAmount: values.tokenAmount,
      });
      if (response.status === "SUCCESS") {
        if (user) {
          dispatch(updateTokenBalance((user.tokenBalance || 0) - values.tokenAmount));
        }
        form.resetFields();
        form.setFieldsValue({
          receiverId: "",
          tokenAmount: 0,
        });
        message.success("Tokens shared successfully");
      } else {
        message.error(response.message || "Failed to share tokens");
      }
    } catch (error) {
      message.error("Failed to share tokens");
    } finally {
      setLoading(false);
    }
  };

  if (!canShareToken) {
    return <Card>You don't have permission to share tokens.</Card>;
  }

  if (loading && !form.isFieldsTouched()) {
    return <ContentSkeleton cardCount={1} rows={4} />;
  }

  return (
    <Card
      title="Share Tokens"
      style={{ maxWidth: 600, margin: "0 auto" }}
      extra={
        <Button onClick={() => navigate(PERMISSIONS.LIST_SELLERS.route)}>
          Back to List
        </Button>
      }
    >
      <Space
        direction="vertical"
        style={{ width: "100%", textAlign: "center" }}
      >
        <div>
          <Text strong>Current Token Balance: </Text>
          <Text>{user?.tokenBalance || 0}</Text>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="receiverId"
            label="Select Receiver"
            rules={[{ required: true, message: "Please select a receiver" }]}
          >
            <Select
              showSearch
              placeholder="Select a receiver"
              optionFilterProp="children"
              onChange={handleReceiverChange}
              loading={loadingReceivers}
            >
              {receivers.map((receiver) => (
                <Option key={receiver.id} value={receiver.id.toString()}>
                  {`${receiver.name} (${receiver.phone})`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="tokenAmount"
            label="Number of Tokens"
            rules={[
              { required: true, message: "Please enter number of tokens" },
              { type: "number", min: 1, message: "Minimum 1 token required" },
              {
                validator: (_, value) =>
                  value <= (user?.tokenBalance || 0)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Insufficient token balance")),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter number of tokens"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Share Tokens
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

export default TokenDistributionPage;
